import type { AnyRouter, RegisteredRouter, SearchParamOptions } from '@tanstack/react-router';

/**
 * The search each page was showing when you last left it — so a "Back to …"
 * link can return you to it.
 *
 * Every list page keeps its text search, filter chips, sort and page in the
 * URL, which is why the browser's Back button already comes back to them. A
 * "← Back to customers" link on a detail page does not: it is a fresh
 * navigation to the bare path, so the list reopened unfiltered and the user
 * had to pick their filters again. Those links now pass
 * `search={lastSearch('/customers')}` — most of them via `<BackLink>`.
 *
 * Recorded once, from the router (`rememberSearches` in `main.tsx`), rather
 * than by each page, so a new list page is covered without opting in. Keyed by
 * pathname, so a detail page's tab (`/customers/<id>?tab=…`) is remembered the
 * same way for the links that climb back to it.
 *
 * In memory rather than sessionStorage: a remembered search is only ever
 * replayed into the same bundle whose `validateSearch` produced it, so a deploy
 * that tightens a route's schema can never meet a stale stored value and throw.
 * The cost is that a reload forgets — the link then opens the plain list, as it
 * always used to.
 *
 * The sidebar deliberately does NOT use this: picking a section from the nav
 * is a fresh start, not a return.
 */
const lastSearchByPath = new Map<string, Record<string, unknown>>();

/**
 * Search keys that describe a passing view, not a filter, so they are never
 * replayed: `generate` + `g*` hold the invoice Generate sheet open (a Back link
 * would pop it over the list again), and `version` puts a historical record on
 * screen (a Back link would land on an old version the user had long left).
 */
const TRANSIENT_KEYS = new Set(['generate', 'gMonth', 'gTypes', 'gFreqs', 'gKind', 'version']);

/** Pages outside the signed-in app. Reaching one ends the identity whose filters these were. */
const SIGNED_OUT_PATHS = new Set(['/login', '/no-access']);

/** Every record visited is a pathname; keep the most recent ones, not all of them. */
const MAX_REMEMBERED = 50;

/** `/customers/` and `/customers` are the same page. */
function pathKey(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function without(search: Record<string, unknown>, keys: Iterable<string>): Record<string, unknown> {
  const out = { ...search };
  for (const key of keys) delete out[key];
  return out;
}

/**
 * Start recording. Call once with the app's router; returns the unsubscribe.
 *
 * Each page is recorded as it is LEFT: `onBeforeLoad` fires at the start of
 * every navigation, while `router.stores.matches` still holds the page the user
 * was looking at — committed, rendered, its search already through
 * `validateSearch`. So only what was actually on screen is ever remembered: a
 * navigation that a `beforeLoad` redirects, or that a newer one supersedes,
 * never commits and so is never recorded, and nothing is matched twice.
 *
 * Deliberately not `onResolved`. The React Transitioner emits that only when a
 * render observes the router go pending → idle; a navigation that settles
 * inside a single render (no loaders, a fast machine) never shows as pending,
 * so recordings were silently skipped — the #815 merge failed CI on exactly
 * this, and `last-search.test.tsx` drives the router with nothing rendered to
 * keep it that way. `onBeforeLoad` is emitted by the router core itself,
 * synchronously, on every `router.load()`.
 */
export function rememberSearches(router: AnyRouter): () => void {
  return router.subscribe('onBeforeLoad', ({ toLocation }) => {
    // Runs inside the router's own load: a throw here would stop navigation
    // app-wide for the sake of a convenience, so it is logged and swallowed.
    try {
      const leaving = router.stores.matches.get().at(-1);
      // `searchError` means the page rejected its URL and showed the error
      // screen — replaying that search would only show it again.
      if (leaving && !leaving.searchError) {
        const path = pathKey(leaving.pathname);
        lastSearchByPath.delete(path); // re-insert so Map order is recency
        lastSearchByPath.set(path, without(leaving.search, TRANSIENT_KEYS));
        if (lastSearchByPath.size > MAX_REMEMBERED) {
          const oldest = lastSearchByPath.keys().next().value;
          if (oldest !== undefined) lastSearchByPath.delete(oldest);
        }
      }
      // Signing out (or being bounced) must not hand one person's filters —
      // a delegated `userId`, chosen customers — to whoever signs in next.
      if (SIGNED_OUT_PATHS.has(pathKey(toLocation.pathname))) lastSearchByPath.clear();
    } catch (err) {
      console.error('last-search: could not record the page being left', err);
    }
  });
}

/**
 * The search `to` was last showing, or `{}` if it hasn't been visited since
 * the app loaded. Typed as the `search` option of a `<Link>` / `navigate()` to
 * `to`, so it drops straight in:
 *
 *   <Link to="/customers" search={lastSearch('/customers')}>
 *   <Link to="/customers/$id" params={{ id }} search={lastSearch('/customers/$id', { params: { id } })}>
 *
 * `drop` leaves keys out — after a delete, `page` may point past the end.
 */
export function lastSearch<TTo extends string>(
  to: TTo,
  { params, drop = [] }: { params?: Record<string, string>; drop?: readonly string[] } = {},
): SearchParamOptions<RegisteredRouter, string, TTo>['search'] {
  const pathname = params
    ? to.replace(/\$(\w+)/g, (segment, name: string) => params[name] ?? segment)
    : to;
  return without(lastSearchByPath.get(pathKey(pathname)) ?? {}, drop) as SearchParamOptions<
    RegisteredRouter,
    string,
    TTo
  >['search'];
}
